import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { useDashboardStructure } from '@/features/dashboard/api/queries'
import type { Timeframe } from '@/features/dashboard/api/schemas'
import {
  allMarketIds,
  parseMarkets,
  parseSectionSelection,
  parseViewMode,
  toggleMarket,
  withMemberMarkets,
  writeMarkets,
  writeSectionRange,
  writeSectionTimeframe,
  writeViewMode,
} from './filterState'

/**
 * The dashboard-wide filters, kept in the URL so a view can be shared: the
 * markets and the view mode. All `undefined` or empty until the structure
 * (which says what is on offer) has loaded, or if it offers none.
 */
export const useDashboardFilters = () => {
  const { data: dashboard } = useDashboardStructure()
  const filters = dashboard?.filters
  const [params, setParams] = useSearchParams()

  const selectedMarkets = useMemo(
    () => (filters ? parseMarkets(params, filters) : []),
    [params, filters]
  )
  const allIds = useMemo(
    () => (filters ? allMarketIds(filters) : []),
    [filters]
  )

  const updateMarkets = useCallback(
    (change: (current: string[]) => string[]) => {
      if (!filters) return
      setParams(
        current =>
          writeMarkets(
            current,
            change(parseMarkets(current, filters)),
            filters
          ),
        { replace: true }
      )
    },
    [filters, setParams]
  )
  const toggle = useCallback(
    (id: string) => updateMarkets(current => toggleMarket(current, allIds, id)),
    [updateMarkets, allIds]
  )
  const clearMarkets = useCallback(
    () => updateMarkets(() => allIds),
    [updateMarkets, allIds]
  )

  const viewModes = dashboard?.viewModes ?? []
  const viewMode = dashboard ? parseViewMode(params, dashboard) : undefined
  const setViewMode = useCallback(
    (id: string) => {
      if (!dashboard) return
      setParams(current => writeViewMode(current, id, dashboard), {
        replace: true,
      })
    },
    [dashboard, setParams]
  )

  const isMarketFiltered =
    filters !== undefined && selectedMarkets.length < allIds.length

  return {
    /** The selected market ids; `undefined` when every market is (no filter). */
    markets: isMarketFiltered ? withMemberMarkets(selectedMarkets) : undefined,
    /** The selected market ids, for the chips to show; every one with no filter. */
    selectedMarkets: filters ? selectedMarkets : allIds,
    isMarketFiltered,
    toggleMarket: toggle,
    clearMarkets,
    viewModes,
    /** The selected view mode; `undefined` when the dashboard has none. */
    viewMode,
    setViewMode,
  }
}

/**
 * A section's timeframe and range, kept in the URL. `selection` is
 * `undefined` until the structure has loaded, or if it offers no timeframes.
 */
export const useSectionSelection = (sectionId: string | undefined) => {
  const { data: dashboard } = useDashboardStructure()
  const [params, setParams] = useSearchParams()

  const selection =
    dashboard && sectionId !== undefined
      ? parseSectionSelection(params, sectionId, dashboard)
      : undefined

  const setTimeframe = useCallback(
    (timeframe: Timeframe) => {
      if (!dashboard || sectionId === undefined) return
      setParams(
        current =>
          writeSectionTimeframe(current, sectionId, timeframe, dashboard),
        { replace: true }
      )
    },
    [dashboard, sectionId, setParams]
  )
  const setPeriods = useCallback(
    (periods: number) => {
      if (!dashboard || sectionId === undefined) return
      setParams(
        current => writeSectionRange(current, sectionId, periods, dashboard),
        { replace: true }
      )
    },
    [dashboard, sectionId, setParams]
  )

  return { selection, setTimeframe, setPeriods }
}
